"use client";

import { useRef, useState } from "react";
import { toast } from "sonner";
import { useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";

type IndentSize = "0.5" | "1";
type LineSpacing = "1" | "1.5" | "2";

const NEWLINE_REGEX = /\n/;

function getLineHeight(spacing: LineSpacing): string {
  switch (spacing) {
    case "1":
      return "1.4";
    case "1.5":
      return "2.1";
    case "2":
      return "2.8";
    default:
      return "2.1";
  }
}

function buildHtml(
  entries: string[],
  indent: IndentSize,
  spacing: LineSpacing
): string {
  const indentVal = `${indent}in`;
  const lh = getLineHeight(spacing);
  const lines = entries
    .map(
      (e) =>
        `<p style="padding-left:${indentVal};text-indent:-${indentVal};line-height:${lh};margin:0 0 0.3em 0;">${escapeHtml(e)}</p>`
    )
    .join("\n");
  return lines;
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** English strings. A translated content file overrides these through `ui`. */
export const HANGING_INDENT_FORMATTER_STRINGS = {
  pasteBibliographyEntriesOnePer: "Paste bibliography entries (one per line)",
  indentSize: "Indent size",
  n05Inch: "0.5 inch",
  n1Inch: "1 inch",
  lineSpacing: "Line spacing",
  single: "Single",
  double: "Double",
  preview: "Preview",
  copied: "Copied!",
  copyAsHtml: "Copy as HTML",
  copyAsRichText: "Copy as Rich Text",
  hangingIndentRules: "Hanging Indent Rules",
  apa7thEdition: "APA 7th Edition",
  useA05Inch:
    "Use a 0.5-inch hanging indent for all reference list entries. Double-space the entire reference list. The first line of each entry is flush left, with subsequent lines indented.",
  mla9thEdition: "MLA 9th Edition",
  useA05Inch2:
    "Use a 0.5-inch hanging indent for all Works Cited entries. Double-space the entire list. Do not add extra space between entries.",
  chicagoManualOfStyle: "Chicago Manual of Style",
  useAHangingIndentTypically:
    "Use a hanging indent (typically 0.5 inches) for bibliography entries. Single-space within entries, and add a blank line between entries, or double-space throughout.",
  copyFailedToast: "Couldn't copy to clipboard. Please copy manually.",
} as const;

export default function HangingIndentFormatterWidget() {
  const [input, setInput] = useState("");
  const [indent, setIndent] = useState<IndentSize>("0.5");
  const [spacing, setSpacing] = useState<LineSpacing>("2");
  const [copiedType, setCopiedType] = useState<string | null>(null);
  const previewRef = useRef<HTMLDivElement>(null);

  const entries = input
    .split(NEWLINE_REGEX)
    .map((e) => e.trim())
    .filter((e) => e.length > 0);

  const indentVal = `${indent}in`;
  const lineHeight = getLineHeight(spacing);

  async function copyHtml() {
    const html = buildHtml(entries, indent, spacing);
    if (await writeToClipboard(html)) {
      setCopiedType("html");
      setTimeout(() => setCopiedType(null), 2000);
    } else {
      toast.error(strings.copyFailedToast);
    }
  }

  async function copyRichText() {
    const html = buildHtml(entries, indent, spacing);
    try {
      const blob = new Blob([html], { type: "text/html" });
      await navigator.clipboard.write([
        new ClipboardItem({ "text/html": blob }),
      ]);
      setCopiedType("rich");
      setTimeout(() => setCopiedType(null), 2000);
    } catch {
      // Fallback: copy as plain HTML
      await copyHtml();
    }
  }

  const strings = useToolUi(HANGING_INDENT_FORMATTER_STRINGS);

  return (
    <>
      {/* Input */}
      <div className="space-y-3">
        <label
          className="block font-medium text-foreground text-sm"
          htmlFor="hi-input"
        >
          {strings.pasteBibliographyEntriesOnePer}
        </label>
        <textarea
          className="min-h-[180px] w-full rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:border-foreground/30 focus:outline-none focus:ring-1 focus:ring-foreground/30"
          id="hi-input"
          onChange={(e) => setInput(e.target.value)}
          placeholder={
            "Smith, J. (2024). Title of article. Journal Name, 12(3), 45-67.\nJohnson, A. (2023). Another article title. Science Today, 8(1), 12-25."
          }
          value={input}
        />
      </div>
      {/* Options */}
      <div className="flex flex-wrap items-center gap-6 rounded-lg border border-border bg-muted/30 p-4">
        <div className="space-y-1">
          <span className="block font-medium text-foreground text-sm">
            {strings.indentSize}
          </span>
          <div className="flex gap-3">
            <label className="flex items-center gap-1.5 text-muted-foreground text-sm">
              <input
                checked={indent === "0.5"}
                name="indent"
                onChange={() => setIndent("0.5")}
                type="radio"
              />
              {strings.n05Inch}
            </label>
            <label className="flex items-center gap-1.5 text-muted-foreground text-sm">
              <input
                checked={indent === "1"}
                name="indent"
                onChange={() => setIndent("1")}
                type="radio"
              />
              {strings.n1Inch}
            </label>
          </div>
        </div>

        <div className="space-y-1">
          <span className="block font-medium text-foreground text-sm">
            {strings.lineSpacing}
          </span>
          <div className="flex gap-3">
            <label className="flex items-center gap-1.5 text-muted-foreground text-sm">
              <input
                checked={spacing === "1"}
                name="spacing"
                onChange={() => setSpacing("1")}
                type="radio"
              />
              {strings.single}
            </label>
            <label className="flex items-center gap-1.5 text-muted-foreground text-sm">
              <input
                checked={spacing === "1.5"}
                name="spacing"
                onChange={() => setSpacing("1.5")}
                type="radio"
              />
              1.5
            </label>
            <label className="flex items-center gap-1.5 text-muted-foreground text-sm">
              <input
                checked={spacing === "2"}
                name="spacing"
                onChange={() => setSpacing("2")}
                type="radio"
              />
              {strings.double}
            </label>
          </div>
        </div>
      </div>
      {/* Preview */}
      {entries.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-medium text-foreground text-sm">
              {strings.preview}
            </h2>
            <div className="flex gap-2">
              <button
                className="rounded-md border border-border px-3 py-1 text-muted-foreground text-sm transition-colors hover:bg-muted hover:text-foreground"
                onClick={copyHtml}
                type="button"
              >
                {copiedType === "html" ? strings.copied : strings.copyAsHtml}
              </button>
              <button
                className="rounded-md border border-border px-3 py-1 text-muted-foreground text-sm transition-colors hover:bg-muted hover:text-foreground"
                onClick={copyRichText}
                type="button"
              >
                {copiedType === "rich"
                  ? strings.copied
                  : strings.copyAsRichText}
              </button>
            </div>
          </div>
          <div
            className="rounded-lg border border-border bg-white p-6 text-neutral-800 text-sm dark:bg-neutral-50"
            ref={previewRef}
          >
            {entries.map((entry, i) => (
              <p
                key={`entry-${
                  // biome-ignore lint/suspicious/noArrayIndexKey: stable list order
                  i
                }`}
                style={{
                  paddingLeft: indentVal,
                  textIndent: `-${indentVal}`,
                  lineHeight,
                  margin: "0 0 0.3em 0",
                }}
              >
                {entry}
              </p>
            ))}
          </div>
        </div>
      )}
      {/* Explanation */}
      <section className="space-y-3 rounded-lg border border-border bg-muted/30 p-6">
        <h2 className="font-semibold text-foreground text-lg">
          {strings.hangingIndentRules}
        </h2>
        <div className="space-y-4 text-muted-foreground text-sm leading-relaxed">
          <div>
            <h3 className="font-medium text-foreground">
              {strings.apa7thEdition}
            </h3>
            <p>{strings.useA05Inch}</p>
          </div>
          <div>
            <h3 className="font-medium text-foreground">
              {strings.mla9thEdition}
            </h3>
            <p>{strings.useA05Inch2}</p>
          </div>
          <div>
            <h3 className="font-medium text-foreground">
              {strings.chicagoManualOfStyle}
            </h3>
            <p>{strings.useAHangingIndentTypically}</p>
          </div>
        </div>
      </section>
      {/* Related tools */}
    </>
  );
}
